/**
 * Component definitions for the scrolly rectangle demo
 * Components represent different feature layers
 */
import type { PrimitiveAtom, atom } from "jotai";
import { World } from "#scripts/vmc/WorldState.ts";

/**
 * Base value component
 */
export class CValue extends World.Component("value")<
  CValue,
  {
    valueAtom: PrimitiveAtom<string>;
  }
>() {}

/**
 * Feature flags that control which UI layers are visible
 */
export class CFeatureFlags extends World.Component("feature-flags")<
  CFeatureFlags,
  {
    flagsAtom: PrimitiveAtom<Set<string>>;
  }
>() {}

/**
 * Validation components
 */
export class CValidationState extends World.Component("validation-state")<
  CValidationState,
  {
    isValidAtom: PrimitiveAtom<boolean>;
    errorAtom: PrimitiveAtom<string | null>;
  }
>() {}

/**
 * Undo/redo stack
 */
export class CUndoStack extends World.Component("undo-stack")<
  CUndoStack,
  {
    historyAtom: PrimitiveAtom<string[]>;
    indexAtom: PrimitiveAtom<number>;
  }
>() {}

/**
 * Version comparison
 */
export class CVersionComparison extends World.Component("version-comparison")<
  CVersionComparison,
  {
    baselineAtom: PrimitiveAtom<string>;
    hasChangesAtom: PrimitiveAtom<boolean>;
  }
>() {}

/**
 * Change origin tracking
 */
export class CChangeOrigin extends World.Component("change-origin")<
  CChangeOrigin,
  {
    authorAtom: PrimitiveAtom<string>;
    timestampAtom: PrimitiveAtom<Date>;
  }
>() {}

/**
 * Collaborative presence
 */
export class CCollaborators extends World.Component("collaborators")<
  CCollaborators,
  {
    cursorsAtom: ReturnType<typeof atom<Array<{ userId: string; position: number; color: string }>>>;
  }
>() {}

/**
 * Selection state
 */
export class CSelection extends World.Component("selection")<
  CSelection,
  {
    selectedRangeAtom: PrimitiveAtom<[number, number] | null>;
  }
>() {}

/**
 * Focus state
 */
export class CFocus extends World.Component("focus")<
  CFocus,
  {
    focusPositionAtom: PrimitiveAtom<number>;
    isFocusedAtom: PrimitiveAtom<boolean>;
  }
>() {}

/**
 * Context-aware actions
 */
export class CActions extends World.Component("actions")<
  CActions,
  {
    availableActionsAtom: ReturnType<typeof atom<Array<{ id: string; label: string; keybinding: string }>>>;
  }
>() {}

/**
 * AI suggestions
 */
export class CSuggestions extends World.Component("suggestions")<
  CSuggestions,
  {
    suggestionsAtom: ReturnType<typeof atom<Array<{ text: string; confidence: number }>>>;
  }
>() {}

/**
 * Inline visualization
 */
export class CInlineChart extends World.Component("inline-chart")<
  CInlineChart,
  {
    dataPointsAtom: PrimitiveAtom<number[]>;
  }
>() {}

/**
 * Entity class for the scrolly rectangle
 */
export class ScrollyRectangleEntity extends World.Entity("scrolly-rectangle", {
  components: [
    CValue,
    CFeatureFlags,
    CValidationState,
    CUndoStack,
    CVersionComparison,
    CChangeOrigin,
    CCollaborators,
    CSelection,
    CFocus,
    CActions,
    CSuggestions,
    CInlineChart,
  ],
})<ScrollyRectangleEntity>() {}
